import { For, Show, createSignal, onMount } from "solid-js";
import { formatDate, type AdminApi, type SystemInfo } from "./api";

const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;

export function SystemView(props: { api: AdminApi; guard: <T>(work: () => Promise<T>) => Promise<T> }) {
  const [info, setInfo] = createSignal<SystemInfo | null>(null);
  const [error, setError] = createSignal("");
  const [fixBusy, setFixBusy] = createSignal(false);
  const [fixResult, setFixResult] = createSignal<string>("");

  onMount(async () => {
    try {
      setInfo(await props.guard(() => props.api.system()));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Laden fehlgeschlagen");
    }
  });

  const fixClassSchoolIds = async () => {
    setFixBusy(true);
    setFixResult("");
    try {
      const result = await props.guard(() => props.api.fixClassSchoolIds());
      setFixResult(result.fixed === 0 ? "Alles sauber – keine Klassen ohne Schulzuordnung gefunden." : `${result.fixed} Klasse${result.fixed === 1 ? "" : "n"} aktualisiert.`);
    } catch (cause) {
      setFixResult(`Fehler: ${cause instanceof Error ? cause.message : "Unbekannt"}`);
    } finally {
      setFixBusy(false);
    }
  };

  const backupStale = (lastSuccessAt: string | null) => !lastSuccessAt || Date.now() - new Date(lastSuccessAt).getTime() > TWO_DAYS_MS;

  return (
    <section>
      <h2>System</h2>
      <Show when={error()}><p class="adm-error" role="alert">{error()}</p></Show>
      <Show when={info()}>
        {(system) => (
          <>
            <div class="adm-card">
              <dl class="adm-facts">
                <dt>Version</dt><dd>{system().version ?? "nicht bekannt"}</dd>
                <dt>Node</dt><dd>{system().node}</dd>
                <dt>Migrationen</dt><dd>{system().migrations.length} angewendet, zuletzt {system().migrations.at(-1) ?? "–"}</dd>
              </dl>
            </div>
            <div class="adm-card">
              <h3 style={{ margin: "0 0 10px", "font-size": "1rem" }}>Server-Backup</h3>
              <Show when={system().backup} fallback={<p class="adm-warn">Keine Statusdatei gefunden. Läuft das Backup (scripts/backup/install-server.sh)?</p>}>
                {(backup) => (
                  <dl class="adm-facts">
                    <dt>Letztes Ergebnis</dt><dd class={backup().result === "ok" ? "adm-ok" : "adm-warn"}>{backup().result === "ok" ? "in Ordnung" : "Fehler"}{backup().message ? `: ${backup().message}` : ""}</dd>
                    <dt>Zuletzt erfolgreich</dt><dd class={backupStale(backup().lastSuccessAt) ? "adm-warn" : ""}>{formatDate(backup().lastSuccessAt)}{backupStale(backup().lastSuccessAt) ? " (älter als 2 Tage)" : ""}</dd>
                    <dt>Letzter Lauf</dt><dd>{formatDate(backup().finishedAt)}</dd>
                    <dt>Restore-Test</dt><dd>{backup().restoreTest ?? "–"}</dd>
                  </dl>
                )}
              </Show>
              <Show when={system().migrations.length > 0}>
                <ul class="adm-list"><For each={system().migrations.slice(-5)}>{(name) => <li>{name}</li>}</For></ul>
              </Show>
            </div>
            <div class="adm-card">
              <h3 style={{ margin: "0 0 6px", "font-size": "1rem" }}>Wartung</h3>
              <p style={{ margin: "0 0 10px", "font-size": "0.875rem", color: "#888" }}>Einmalige Reparatur-Funktionen für Datenkonsistenz.</p>
              <div style={{ display: "flex", "align-items": "center", gap: "12px", "flex-wrap": "wrap" }}>
                <button class="adm-btn" type="button" disabled={fixBusy()} onClick={() => void fixClassSchoolIds()}>
                  {fixBusy() ? "Läuft …" : "Klassen-Schulzuordnung reparieren"}
                </button>
                <Show when={fixResult()}>
                  <span style={{ "font-size": "0.875rem" }}>{fixResult()}</span>
                </Show>
              </div>
              <p style={{ margin: "8px 0 0", "font-size": "0.8rem", color: "#888" }}>
                Setzt school_id für alle Klassen, deren Lehrkraft einer Schule angehört, aber noch keine Schulzuordnung haben. Idempotent – kann beliebig oft ausgeführt werden.
              </p>
            </div>
          </>
        )}
      </Show>
    </section>
  );
}
